import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, TrendingUp, Clock } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DyvysHomePage() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dyvys/series`);
      const data = await response.json();
      
      // Pour chaque série, récupérer le prix minimum
      const seriesWithPrices = await Promise.all(
        (data.series || []).map(async (serie) => {
          try {
            const detailResponse = await fetch(`${API_URL}/api/dyvys/series/${serie.series_id}`);
            const detailData = await detailResponse.json();
            
            // Trouver le prix minimum parmi les épisodes non gratuits
            const paidEpisodes = detailData.episodes.filter(ep => !ep.is_free);
            const minPrice = paidEpisodes.length > 0 
              ? Math.min(...paidEpisodes.map(ep => ep.price_usd || 0))
              : 0;
            
            return {
              ...serie,
              minPrice: minPrice,
              episodeCount: detailData.episodes.length
            };
          } catch (error) {
            return {
              ...serie,
              minPrice: 0,
              episodeCount: 0
            };
          }
        })
      );
      
      setSeries(seriesWithPrices);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Impossible de charger les séries");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white">
      {/* Header */}
      <header className="border-b border-purple-800/50 backdrop-blur-sm bg-black/30">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">
                DYVY'S Afrique
              </h1>
              <p className="text-gray-300 text-sm md:text-base mt-1">
                Plateforme de vente de vidéos
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="border-purple-500 text-purple-300 hover:bg-purple-500/20"
              >
                Connexion
              </Button>
              <Button
                onClick={() => navigate("/dyvys/admin/login")}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Admin
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Découvrez des <span className="bg-gradient-to-r from-purple-400 to-pink-600 bg-clip-text text-transparent">vidéos</span> captivantes
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Achetez et regardez vos vidéos préférées. Clips musicaux, comédies et animations.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-2 bg-purple-500/20 rounded-full px-6 py-3 border border-purple-500/30">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              <span className="text-sm font-medium">{series.length} Séries Disponibles</span>
            </div>
            <div className="flex items-center gap-2 bg-pink-500/20 rounded-full px-6 py-3 border border-pink-500/30">
              <Play className="w-5 h-5 text-pink-400" />
              <span className="text-sm font-medium">Streaming HD</span>
            </div>
          </div>
        </div>
      </section>

      {/* Series Catalog */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <h3 className="text-3xl font-bold mb-8">
            Catalogue de Séries
          </h3>

          {series.length === 0 ? (
            <div className="text-center py-20">
              <Clock className="w-16 h-16 text-purple-400 mx-auto mb-4 opacity-50" />
              <p className="text-xl text-gray-400">Aucune série disponible pour le moment</p>
              <p className="text-gray-500 mt-2">Revenez bientôt pour découvrir nos vidéos !</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {series.map((item) => (
                <Card
                  key={item.series_id}
                  className="bg-gradient-to-br from-gray-800/50 to-purple-900/30 border-purple-500/30 hover:border-purple-500 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/20 cursor-pointer"
                  onClick={() => navigate(`/dyvys/series/${item.series_id}`)}
                >
                  <CardHeader className="p-0">
                    {item.cover_image_url ? (
                      <img
                        src={item.cover_image_url}
                        alt={item.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center rounded-t-lg">
                        <Play className="w-16 h-16 text-white opacity-50" />
                      </div>
                    )}
                  </CardHeader>
                  <CardContent className="p-4">
                    <CardTitle className="text-lg text-white mb-2">{item.title}</CardTitle>
                    {item.category && (
                      <div className="inline-block bg-purple-500/20 text-purple-300 text-xs px-3 py-1 rounded-full mb-2">
                        {item.category}
                      </div>
                    )}
                    <CardDescription className="text-gray-400 text-sm line-clamp-2 mb-3">
                      {item.description || "Découvrez cette série passionnante..."}
                    </CardDescription>
                    {item.minPrice > 0 && (
                      <div className="flex items-center gap-2 text-green-400 font-semibold">
                        <span className="text-sm">À partir de</span>
                        <span className="text-lg">${item.minPrice} USD</span>
                      </div>
                    )}
                    {item.episodeCount > 0 && (
                      <p className="text-gray-500 text-xs mt-1">
                        {item.episodeCount} vidéo{item.episodeCount > 1 ? 's' : ''}
                      </p>
                    )}
                  </CardContent>
                  <CardFooter className="p-4 pt-0">
                    <Button
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dyvys/series/${item.series_id}`);
                      }}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Voir les épisodes
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-purple-800/50 backdrop-blur-sm bg-black/30 py-8 mt-20">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2024 DYVY'S Afrique - Tous droits réservés
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Plateforme de vente de vidéos
          </p>
        </div>
      </footer>
    </div>
  );
}
